import { useMemo, useRef, useState } from 'react';
import { Trash2, Upload } from 'lucide-react';
import { useMultipartTransfer, type MultipartTransport } from '@ludolio/shared-games';
import {
  Button,
  Progress,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@ludolio/shared-ui';
import { BuildStatusBadge, SectionTitle } from '@/components/common';
import { getErrorMessage, labsApi } from '@/lib/api';
import { PLATFORM_OPTIONS, formatBytes, formatDate, platformLabel } from '@/lib/format';
import type { GamePlatform, LabBuild, LabProjectDetail, LabRelease } from '@/lib/types';

const MAX_BUILD_BYTES = 5 * 1024 * 1024 * 1024;

function wrap<T>(promise: Promise<T>): Promise<T> {
  return promise.catch((error) => {
    throw new Error(getErrorMessage(error, 'Upload failed'));
  });
}

export function BuildsPanel({
  release,
  reusableBuilds,
  editable,
  onChange,
}: {
  release: LabRelease;
  reusableBuilds: LabBuild[];
  editable: boolean;
  onChange: (detail: LabProjectDetail) => void;
}) {
  const transfer = useMultipartTransfer();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [platform, setPlatform] = useState<GamePlatform>('windows-x64');
  const [busyBuildId, setBusyBuildId] = useState<string | null>(null);

  const attachedIds = useMemo(() => new Set(release.builds.map((build) => build.id)), [release.builds]);
  const reusable = reusableBuilds.filter((build) => !attachedIds.has(build.id));

  const run = async (buildId: string | null, action: () => Promise<LabProjectDetail>) => {
    setBusyBuildId(buildId);
    try {
      onChange(await action());
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyBuildId(null);
    }
  };

  const startUpload = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.zip')) {
      toast.error('Upload the build as a .zip file');
      return;
    }
    if (file.size > MAX_BUILD_BYTES) {
      toast.error('Builds must be 5 GB or smaller');
      return;
    }

    const transport: MultipartTransport<string> = {
      initiate: async ({ filename, fileSize, totalParts, checksum }) => {
        const result = await wrap(labsApi.initiateBuild({ releaseId: release.id, platform, filename, fileSize, totalParts, checksum }));
        return result.buildId;
      },
      getPartUrls: async (buildId, partNumbers) => (await wrap(labsApi.getBuildPartUrls(buildId, partNumbers))).urls,
      complete: async (buildId, { parts }) => {
        onChange(await wrap(labsApi.completeBuild(buildId, release.id, parts)));
      },
      abort: async (buildId) => {
        await labsApi.abortBuild(buildId);
      },
    };

    const ok = await transfer.upload(file, transport);
    if (ok) {
      toast.success('Upload complete. The security check has started.');
      transfer.reset();
    }
  };

  const uploadPhaseLabel = {
    idle: '',
    initiating: 'Preparing upload…',
    uploading: `Uploading ${transfer.progress.percentComplete}%`,
    completing: 'Finishing upload…',
    complete: 'Upload complete',
    error: transfer.progress.error ?? 'Upload failed',
    aborted: 'Upload cancelled',
  }[transfer.progress.phase];

  return (
    <section>
      <SectionTitle>Builds</SectionTitle>

      {release.builds.length === 0 && (
        <p className="mb-4 text-sm text-muted-foreground">Upload a playable build as a .zip file for each platform you support.</p>
      )}

      {release.builds.length > 0 && (
        <ul className="mb-5 divide-y rounded-lg border">
          {release.builds.map((build) => (
            <li key={build.id} className="grid gap-3 p-4 sm:grid-cols-[13rem_minmax(0,1fr)_auto] sm:items-center">
              <div className="min-w-0">
                <p className="font-medium">{platformLabel(build.platform)}</p>
                <p className="text-xs text-muted-foreground">{formatBytes(build.fileSize)} · {formatDate(build.createdAt)}</p>
              </div>

              <div className="min-w-0">
                {build.status === 'passed' && build.executableCandidates.length > 1 && editable ? (
                  <Select
                    value={build.executablePath ?? undefined}
                    onValueChange={(value) => void run(build.id, () => labsApi.selectExecutable(build.id, value))}
                    disabled={busyBuildId === build.id}
                  >
                    <SelectTrigger aria-label={`Program to launch for ${platformLabel(build.platform)}`}>
                      <SelectValue placeholder="Choose the program to launch" />
                    </SelectTrigger>
                    <SelectContent>
                      {build.executableCandidates.map((candidate) => (
                        <SelectItem key={candidate} value={candidate}>{candidate}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p
                    className={build.status === 'passed' ? 'truncate text-sm text-muted-foreground' : 'text-sm text-muted-foreground [overflow-wrap:anywhere]'}
                    title={build.executablePath ?? undefined}
                  >
                    {build.status === 'passed' ? build.executablePath ?? 'No program selected' : build.failureReason ?? ''}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between gap-2 sm:justify-end">
                <BuildStatusBadge status={build.status} />
                {editable && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${platformLabel(build.platform)} build`}
                    disabled={busyBuildId === build.id}
                    onClick={() => void run(build.id, () => labsApi.removeBuild(release.id, build.platform))}
                  >
                    <Trash2 />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editable && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={platform} onValueChange={(value) => setPlatform(value as GamePlatform)} disabled={transfer.isUploading}>
              <SelectTrigger className="w-56" aria-label="Build platform">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLATFORM_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip,application/zip"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) void startUpload(file);
              }}
            />
            {transfer.isUploading ? (
              <Button variant="outline" onClick={() => void transfer.abort()}>Cancel upload</Button>
            ) : (
              <Button onClick={() => fileInputRef.current?.click()}>
                <Upload />
                Upload build
              </Button>
            )}
          </div>

          {transfer.progress.phase !== 'idle' && (
            <div className="space-y-2" aria-live="polite">
              {transfer.isUploading && <Progress value={transfer.progress.percentComplete} />}
              <p className={transfer.isError ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>{uploadPhaseLabel}</p>
            </div>
          )}

          {reusable.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              <Select onValueChange={(buildId) => void run(buildId, () => labsApi.attachBuild(release.id, buildId))}>
                <SelectTrigger className="w-full sm:w-96" aria-label="Use an earlier build">
                  <SelectValue placeholder="Use an earlier build" />
                </SelectTrigger>
                <SelectContent>
                  {reusable.map((build) => (
                    <SelectItem key={build.id} value={build.id}>
                      {platformLabel(build.platform)} · {formatBytes(build.fileSize)} · {formatDate(build.createdAt)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
